import React, {
 FunctionComponent, useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { FormTemplateSection } from 'data/template/template.types';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/contact/contact.actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import { Contact } from 'data/contact/contact.types';
import adaptTemplateVisibilityMode from 'routes/Contact/utils/contactTemplate.adapter';
import ContactForm, { ContactFormRef } from '../../components/ContactForm';
import { contactTemplate } from '../../templates/contactTemplate';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedContact: contactSelectors.getSelectedContact(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  contact: contactSelectors.getContact(state)
});

const mapDispatch = {
  editContact: actions.goToContactEdit,
  goToContactList: actions.goToContactList,
  getContact: actions.getContactById
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const ContactEdit: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedContact,
  history,
  isAuditEnabled,
  userId,
  userName,
  contact,
  editContact,
  goToContactList,
  getContact
}: Props) => {
  const [template, setTemplate] = useState(
    adaptTemplateVisibilityMode(contactTemplate, 'view')
  );
  const [formData, setFormData] = useState<Partial<Contact>>({});
  const contactFormRef = useRef<ContactFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const id = selectedContact[0]?.id;

    if (id) {
      getContact(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedContact, getContact]);

  useEffect(() => {
    if (contact != null) {
      setFormData(contact);

      let clonedTemplate = cloneDeep(contactTemplate);
      clonedTemplate.formId = Date.now().toString();

      clonedTemplate = adaptTemplateVisibilityMode(contactTemplate, 'view');

      setTemplate(clonedTemplate);
    }
  }, [contact]);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value
    });
  };

  const handleClickCancel = () => goToContactList(history);

  const handleClickEdit = () => editContact(history);

  if (!contact) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleClickCancel}
          onClickClose={handleClickCancel}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ContactEdit">
      {renderFormPanel('View Contact')}
      <div
        style={{
          paddingBottom: '25px',
          fontSize: '14px',
          color: 'rgba(24, 32, 38, 0.7)'
        }}
      >
        When entering contacts, please, provide at least one means of contact.
      </div>
      {template && (
        <ContactForm
          data={formData}
          template={template}
          ref={contactFormRef}
          onFieldUpdate={handleFieldUpdate}
        />
      )}
    </div>
  );
};

export default withRouter(connector(ContactEdit));
